import { format } from 'date-fns';

import {
  CUSTOM_INPUTS,
  DATE_CUSTOM_LABEL_KEY,
  RISK_CUSTOM_LABEL_KEY,
} from './constants';
import { type OptionsMap } from './SodRiskScoreFilter';

export const isStrictNumeric = (value: string): boolean => {
  if (value === '') {
    return true;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const isNumeric = /^-?\d+(\.\d+)?$/.test(trimmed);

    return trimmed !== '' && isNumeric;
  }

  return false;
};

export const isLimitFollowed = (value: number) => {
  if (value >= 0) {
    return true;
  }

  return false;
};

export const isOrderFollowed = (
  name: string,
  value: number,
  optionsMap: OptionsMap
) => {
  if (name === CUSTOM_INPUTS.START) {
    const endValue = Number(optionsMap.custom[CUSTOM_INPUTS.END]);
    const isEndEmpty = Boolean(optionsMap.custom[CUSTOM_INPUTS.END] === '');

    if ((!isEndEmpty && value < endValue) || isEndEmpty) {
      return true;
    }

    return false;
  }

  const startValue = Number(optionsMap.custom[CUSTOM_INPUTS.START]);
  const isStartEmpty = Boolean(optionsMap.custom[CUSTOM_INPUTS.END] === '');

  if ((!isStartEmpty && value > startValue) || isStartEmpty) {
    return true;
  }

  return false;
};

export const getRiskFilterOption = <T extends string | number | null>(
  start: T,
  end: T
) => {
  const startLabel = start === null || start === '' ? '0' : String(start);
  const endLabel = end === null || end === '' ? '100' : String(end);
  const value = `[${start && start !== '' ? start : 'null'},${
    end && end !== '' ? end : 'null'
  }]`;

  return {
    label: `${RISK_CUSTOM_LABEL_KEY} (${startLabel} to ${endLabel})`,
    value,
  };
};

export const shiftDateString = (dayOffset: number) => {
  const originalDate = new Date();

  originalDate.setDate(originalDate.getDate() + dayOffset);

  return originalDate;
};

export const getDaysFromToday = (dateInput: Date | undefined | null) => {
  if (!dateInput) {
    return null;
  }

  const today = new Date();
  const targetDate = new Date(dateInput);

  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const msInOneDay = 1000 * 60 * 60 * 24;
  const diffInMs = targetDate.getTime() - today.getTime();

  return Math.round(diffInMs / msInOneDay);
};

export const getCustomRangeLabel = <T extends number | null>(
  startDays: T,
  endDays: T,
  dateFormat: string
): string => {
  if (endDays === 0 || endDays === null) {
    const startDateString = shiftDateString(Number(startDays));
    const date = new Date(startDateString);
    const startLabel = format(date, dateFormat);

    return `${DATE_CUSTOM_LABEL_KEY} (${startLabel})`;
  }

  if (startDays === 0 || startDays === null) {
    const endDateString = shiftDateString(Number(endDays));
    const date = new Date(endDateString);
    const endLabel = format(date, dateFormat);

    return `${DATE_CUSTOM_LABEL_KEY} (${endLabel})`;
  }

  const startDateString = shiftDateString(Number(startDays));
  const startDate = new Date(startDateString);
  const startLabel = format(startDate, dateFormat);
  const endDateString = shiftDateString(Number(endDays));
  const endDate = new Date(endDateString);
  const endLabel = format(endDate, dateFormat);

  return `${DATE_CUSTOM_LABEL_KEY} (${startLabel} to ${endLabel})`;

  // Switched to above format -- keeping for future reference
  // const formatRange = (value: number): string => {
  //   const abs = Math.abs(value);
  //   const isPast = value < 0;

  //   if (abs === 1) {
  //     return isPast ? 'Last 24 hours' : 'Next 24 hours';
  //   }

  //   if (abs < 1) {
  //     const hours = abs * 24;

  //     return isPast ? `Last ${hours} hours` : `Next ${hours} hours`;
  //   }

  //   return isPast ? `Last ${abs} days` : `Next ${abs} days`;
  // };

  // if (startDays === 0 || startDays === null) {
  //   const endLabel = formatRange(endDays ?? 0);

  //   return `${DATE_CUSTOM_LABEL_KEY} (${endLabel})`;
  // }

  // if (endDays === 0 || endDays === null) {
  //   const startLabel = formatRange(startDays);

  //   return `${DATE_CUSTOM_LABEL_KEY} (${startLabel})`;
  // }

  // const startLabel = formatRange(startDays);
  // const endLabel = formatRange(endDays);

  // return `${DATE_CUSTOM_LABEL_KEY} (${startLabel} to ${endLabel})`;
};

export const getDateFromNumber = (num: number): string => {
  // Get today's date
  const today = new Date();

  // Calculate the target date
  today.setDate(today.getDate() + num);

  // Format the date as yyyy-MM-dd
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
